<template>
  <div v-if="loading">
    <spinner-view />
  </div>
  <div v-else-if="fetchError" class="space-y-4">
    <nav class="flex items-center text-sm text-neutral-500 dark:text-neutral-400" aria-label="breadcrumb">
      <router-link :to="{ name: 'Users' }" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
        Users
      </router-link>
      <span class="material-icons text-sm mx-1" aria-hidden="true">chevron_right</span>
      <span class="text-neutral-900 dark:text-neutral-100 font-medium">User</span>
    </nav>
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700">
      <ErrorState title="Couldn't load this user" :message="fetchError" @retry="fetchUser" />
    </div>
  </div>
  <div v-else-if="user" class="space-y-6">
    <!-- Breadcrumb -->
    <nav class="flex items-center text-sm text-neutral-500 dark:text-neutral-400" aria-label="breadcrumb">
      <router-link :to="{ name: 'Users' }" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
        Users
      </router-link>
      <span class="material-icons text-sm mx-1" aria-hidden="true">chevron_right</span>
      <span class="text-neutral-900 dark:text-neutral-100 font-medium">{{ user.displayName }}</span>
    </nav>

    <div class="flex items-center justify-between">
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">{{ user.displayName }}</h1>
      <button
        @click="confirmDeleteUser"
        class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
      >
        Delete User
      </button>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- User Details Card -->
      <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100">User Details</h2>
          <button v-if="!editMode" @click="editMode = true"
            class="px-3 py-1.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors text-sm font-medium">
            Edit
          </button>
          <button v-else @click="cancelEdit"
            class="px-3 py-1.5 bg-neutral-200 dark:bg-neutral-600 text-neutral-700 dark:text-neutral-200 rounded-lg hover:bg-neutral-300 dark:hover:bg-neutral-500 transition-colors text-sm font-medium">
            Cancel
          </button>
        </div>

        <div v-if="!editMode" class="space-y-3">
          <div class="flex justify-between py-2 border-b border-neutral-100 dark:border-neutral-700">
            <span class="text-sm font-medium text-neutral-600 dark:text-neutral-400">UID</span>
            <span class="text-sm text-neutral-900 dark:text-neutral-100 font-mono">{{ user.uid }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-neutral-100 dark:border-neutral-700">
            <span class="text-sm font-medium text-neutral-600 dark:text-neutral-400">Display Name</span>
            <span class="text-sm text-neutral-900 dark:text-neutral-100">{{ user.displayName }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-neutral-100 dark:border-neutral-700">
            <span class="text-sm font-medium text-neutral-600 dark:text-neutral-400">Email</span>
            <a :href="`mailto:${user.email}`" class="text-sm text-blue-600 dark:text-blue-400 hover:underline">{{ user.email }}</a>
          </div>
          <div class="flex justify-between py-2 border-b border-neutral-100 dark:border-neutral-700">
            <span class="text-sm font-medium text-neutral-600 dark:text-neutral-400">Role</span>
            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
              :class="user.userRole === 'ADMIN' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'">
              <span class="inline-block w-1.5 h-1.5 rounded-full" :class="user.userRole === 'ADMIN' ? 'bg-violet-500' : 'bg-blue-500'"></span>
              {{ user.userRole }}
            </span>
          </div>
          <div class="flex justify-between py-2">
            <span class="text-sm font-medium text-neutral-600 dark:text-neutral-400">Created</span>
            <span class="text-sm text-neutral-900 dark:text-neutral-100">{{ user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-' }}</span>
          </div>
        </div>

        <!-- Edit Form -->
        <form v-else @submit.prevent="handleUpdate" class="space-y-4">
          <div>
            <label for="edit-display-name" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">Display Name</label>
            <input id="edit-display-name" v-model="editForm.displayName" type="text" required
              class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100" />
          </div>
          <div>
            <label for="edit-user-role" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">Role</label>
            <select id="edit-user-role" v-model="editForm.userRole"
              class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100">
              <option value="ADMIN">ADMIN</option>
              <option value="USER">USER</option>
            </select>
          </div>
          <div v-if="updateError" role="alert" class="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-3 rounded">
            <p class="text-sm text-red-700 dark:text-red-300">{{ updateError }}</p>
          </div>
          <button type="submit" :disabled="isSubmitting"
            class="w-full px-4 py-2.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors font-medium disabled:opacity-50">
            {{ isSubmitting ? 'Saving...' : 'Save Changes' }}
          </button>
        </form>
      </div>

      <!-- Password Reset Card -->
      <div class="space-y-6">
        <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6">
          <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Reset Password</h2>
          <form @submit.prevent="handleResetPassword" class="space-y-4">
            <div>
              <label for="reset-password" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">New Password</label>
              <div class="relative">
                <input id="reset-password" v-model="newPassword" :type="showPassword ? 'text' : 'password'" required
                  minlength="6" autocomplete="new-password" placeholder="Min 6 characters"
                  class="w-full px-4 py-2.5 pr-11 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100" />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  class="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                >
                  <span class="material-icons text-[20px]" aria-hidden="true">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
                </button>
              </div>
            </div>
            <div v-if="passwordError" role="alert" class="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-3 rounded">
              <p class="text-sm text-red-700 dark:text-red-300">{{ passwordError }}</p>
            </div>
            <button type="submit" :disabled="resettingPassword"
              class="w-full px-4 py-2.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors font-medium disabled:opacity-50">
              {{ resettingPassword ? 'Resetting...' : 'Reset Password' }}
            </button>
          </form>
        </div>

        <!-- Assigned Sites Card -->
        <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6">
          <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Assigned Sites</h2>
          <div class="space-y-2 mb-4">
            <label v-for="site in allSites" :key="site.siteId"
              class="flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors"
              :class="isSiteAssigned(site.siteId)
                ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20'
                : 'border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700'">
              <input type="checkbox" :checked="isSiteAssigned(site.siteId)"
                @change="toggleSite(site)"
                class="w-4 h-4 text-primary-600 rounded border-neutral-300 dark:border-neutral-600 focus:ring-primary-500" />
              <span class="text-sm text-neutral-900 dark:text-neutral-100 font-medium">{{ site.siteName }}</span>
            </label>
          </div>
          <button @click="handleSaveSites" :disabled="savingSites"
            class="w-full px-4 py-2.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors font-medium disabled:opacity-50">
            {{ savingSites ? 'Saving...' : 'Save Site Assignments' }}
          </button>
          <div v-if="sitesError" role="alert" class="mt-3 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-3 rounded">
            <p class="text-sm text-red-700 dark:text-red-300">{{ sitesError }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation -->
    <Modal :show="showDeleteConfirm" title="Delete User" size="sm" @close="showDeleteConfirm = false">
      <p class="text-sm text-neutral-700 dark:text-neutral-300 mb-2">
        Are you sure you want to permanently delete <strong>{{ user.displayName }}</strong>?
      </p>
      <p class="text-xs text-red-600 dark:text-red-400 mb-4">
        This removes both the Firebase Auth account and the Firestore profile.
      </p>
      <div v-if="deleteError" role="alert" class="mb-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-3 rounded">
        <p class="text-sm text-red-700 dark:text-red-300">{{ deleteError }}</p>
      </div>
      <div class="flex justify-end gap-3">
        <button @click="showDeleteConfirm = false"
          class="px-4 py-2 bg-neutral-200 dark:bg-neutral-600 text-neutral-700 dark:text-neutral-200 rounded-lg hover:bg-neutral-300 dark:hover:bg-neutral-500 transition-colors font-medium text-sm">
          Cancel
        </button>
        <button @click="handleDelete" :disabled="isSubmitting"
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm disabled:opacity-50">
          {{ isSubmitting ? 'Deleting...' : 'Delete' }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import axios from "axios";
import getCollection from "@/composables/getCollection";
import useToast from "@/composables/useToast";
import SpinnerView from "@/components/SpinnerView.vue";
import Modal from "@/components/ui/Modal.vue";
import ErrorState from "@/components/ui/ErrorState.vue";

export default {
  components: { SpinnerView, Modal, ErrorState },
  props: ["id"],
  setup(props) {
    const store = useStore();
    const router = useRouter();
    const apiUrl = store.state.apiUrl;
    const { showToast } = useToast();

    const user = ref(null);
    const loading = ref(true);
    const fetchError = ref(null);
    const editMode = ref(false);
    const isSubmitting = ref(false);
    const updateError = ref(null);
    const deleteError = ref(null);
    const showDeleteConfirm = ref(false);

    // Password reset
    const newPassword = ref("");
    const showPassword = ref(false);
    const resettingPassword = ref(false);
    const passwordError = ref(null);

    // Sites
    const assignedSites = ref([]);
    const savingSites = ref(false);
    const sitesError = ref(null);

    const { documents: siteDocs } = getCollection("sites");

    const allSites = computed(() => {
      if (!siteDocs.value) return [];
      return siteDocs.value.map((s) => ({
        siteId: s.siteid || s.siteId || s.id,
        siteName: s.sitename || s.siteName || s.name || s.id,
      }));
    });

    const editForm = ref({ displayName: "", userRole: "" });

    const fetchUser = async () => {
      loading.value = true;
      fetchError.value = null;
      try {
        const response = await axios.get(`${apiUrl}/users/${props.id}`);
        user.value = response.data;
        editForm.value = {
          displayName: user.value.displayName,
          userRole: user.value.userRole,
        };
        assignedSites.value = (user.value.sites || []).map((s) => ({
          siteId: s.siteId,
          siteName: s.siteName,
        }));
      } catch (error) {
        console.error("Error fetching user:", error);
        fetchError.value = error.response?.data?.detail || error.message || "Request failed.";
      } finally {
        loading.value = false;
      }
    };

    const isSiteAssigned = (siteId) => {
      return assignedSites.value.some((s) => s.siteId === siteId);
    };

    const toggleSite = (site) => {
      const idx = assignedSites.value.findIndex((s) => s.siteId === site.siteId);
      if (idx >= 0) {
        assignedSites.value.splice(idx, 1);
      } else {
        assignedSites.value.push({ siteId: site.siteId, siteName: site.siteName });
      }
    };

    const cancelEdit = () => {
      editMode.value = false;
      updateError.value = null;
      // Discard abandoned edits so reopening Edit shows the saved values.
      editForm.value = {
        displayName: user.value.displayName,
        userRole: user.value.userRole,
      };
    };

    const handleUpdate = async () => {
      if (isSubmitting.value) return;
      isSubmitting.value = true;
      updateError.value = null;
      try {
        await axios.put(`${apiUrl}/users/${props.id}`, {
          displayName: editForm.value.displayName,
          userRole: editForm.value.userRole,
        });
        editMode.value = false;
        showToast("success", "User updated");
        await fetchUser();
      } catch (error) {
        updateError.value = error.response?.data?.detail || "Failed to update user.";
        showToast("error", "Failed to update user. Please try again.");
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleResetPassword = async () => {
      if (resettingPassword.value) return;
      resettingPassword.value = true;
      passwordError.value = null;
      try {
        await axios.post(`${apiUrl}/users/${props.id}/reset-password`, {
          newPassword: newPassword.value,
        });
        newPassword.value = "";
        showPassword.value = false;
        showToast("success", "Password reset");
      } catch (error) {
        passwordError.value = error.response?.data?.detail || "Failed to reset password.";
        showToast("error", "Failed to reset password. Please try again.");
      } finally {
        resettingPassword.value = false;
      }
    };

    const handleSaveSites = async () => {
      if (savingSites.value) return;
      savingSites.value = true;
      sitesError.value = null;
      try {
        await axios.put(`${apiUrl}/users/${props.id}`, {
          sites: assignedSites.value,
        });
        showToast("success", "Site assignments updated");
        await fetchUser();
      } catch (error) {
        console.error("Error saving sites:", error);
        sitesError.value = error.response?.data?.detail || "Failed to update site assignments.";
        showToast("error", "Failed to update site assignments. Please try again.");
      } finally {
        savingSites.value = false;
      }
    };

    const confirmDeleteUser = () => {
      deleteError.value = null;
      showDeleteConfirm.value = true;
    };

    const handleDelete = async () => {
      if (isSubmitting.value) return;
      isSubmitting.value = true;
      deleteError.value = null;
      try {
        await axios.delete(`${apiUrl}/users/${props.id}`);
        showDeleteConfirm.value = false;
        showToast("success", "User deleted");
        router.push({ name: "Users" });
      } catch (error) {
        console.error("Error deleting user:", error);
        deleteError.value = error.response?.data?.detail || "Could not delete this user.";
        showToast("error", "Failed to delete user. Please try again.");
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(() => {
      fetchUser();
    });

    return {
      user, loading, fetchError, editMode, isSubmitting, updateError, deleteError,
      editForm, showDeleteConfirm, allSites, assignedSites,
      newPassword, showPassword, resettingPassword, passwordError,
      savingSites, sitesError,
      isSiteAssigned, toggleSite, cancelEdit, fetchUser,
      handleUpdate, handleResetPassword, handleSaveSites,
      confirmDeleteUser, handleDelete,
    };
  },
};
</script>
